/**
 * /echo/scheduler/_trace — internal ops debug page
 * ──────────────────────────────────────────────────────────────────────
 * READ-ONLY, ADMIN-GATED. Shows:
 *   1. Per-tab source states (today / calendar / rooming / waitlist /
 *      recall / insights / settings) — same source-of-truth as
 *      `data-scheduler-trace` on the scheduler page.
 *   2. Endpoint hit counters for every `/api/scheduler/*` URL the
 *      session has touched, bucketed by status family
 *      (`200` / `404` / `5xx` / `0` network / `other`) with the last
 *      status + ISO timestamp.
 *   3. Build hash + page-load timestamp for traceability.
 *
 * NO PHI: counters only carry the URL path + an integer count + the
 * most recent HTTP status. No request bodies, no patient IDs, no
 * appointment payloads.
 *
 * Gated on `canSchedulerAdmin` (= scheduler:admin capability — only
 * `super_admin` / `admin` roles hold it). Non-admin users see the
 * "not authorised" panel with no scheduler internals leaked.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, AlertTriangle, ShieldCheck, Download, CircleStop, Server, ChevronDown, ChevronRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { capabilitiesFor } from '../../lib/permissions';
import { isDemoMode } from '../../lib/modeGuard';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  getEndpointCounters, resetEndpointCounters,
  setEndpointCountersPersistence, isEndpointCountersPersisted,
} from '../../lib/scheduler/endpointCounters';
import {
  SchedulerTraceProvider,
  useSchedulerTraceString,
  useSchedulerTraceStates,
} from '../../lib/scheduler/SchedulerTraceContext';
import { RealEchoScheduler } from './EchoScheduler';
import { submitTraceSnapshot, listTraceSnapshots } from '../../lib/api/opsTraceApi';

// Hard upper bound on capture sweeps — prevents abuse via crafted URLs
// (`/_trace?capture=999` would otherwise spin the scheduler for ages).
const CAPTURE_MAX_RUNS = 5;
const CAPTURE_TAB_DWELL_MS = 800;

// Build hash sentinel shared between the footer and the capture JSON
// payload so they match exactly.
const BUILD_HASH =
  process.env.REACT_APP_BUILD_HASH ||
  process.env.REACT_APP_GIT_SHA ||
  'dev';

const SOURCE_TINT = {
  live:        'bg-emerald-50 text-emerald-700 border-emerald-200',
  loading:     'bg-sky-50 text-sky-700 border-sky-200',
  empty:       'bg-slate-50 text-slate-600 border-slate-200',
  open_only:   'bg-slate-50 text-slate-600 border-slate-200',
  api_missing: 'bg-amber-50 text-amber-700 border-amber-200',
  error:       'bg-rose-50 text-rose-700 border-rose-200',
  forbidden:   'bg-rose-50 text-rose-700 border-rose-200',
  no_location: 'bg-slate-50 text-slate-500 border-slate-200',
};

const StatusCell = ({ value, label, tint }) => (
  <span
    className={`inline-block min-w-[2.5rem] text-center text-[11px] tabular-nums rounded-md border px-1.5 py-0.5 ${tint}`}
    data-testid={`trace-status-${label}`}
  >
    {value || 0}
  </span>
);

const SchedulerTraceInner = () => {
  const trace = useSchedulerTraceString();
  const traceStates = useSchedulerTraceStates();
  const location = useLocation();
  const navigate = useNavigate();
  const [counters, setCounters] = useState(() => getEndpointCounters());
  const [nowIso, setNowIso] = useState(() => new Date().toISOString());
  const [persisted, setPersisted] = useState(() => isEndpointCountersPersisted());

  // Parse ?capture=N. Default OFF (zero / NaN / non-positive → no capture).
  // Cap at CAPTURE_MAX_RUNS to prevent abuse via crafted URLs.
  const requestedCaptureRuns = useMemo(() => {
    const raw = new URLSearchParams(location.search).get('capture');
    if (raw === null) return 0;
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return Math.min(n, CAPTURE_MAX_RUNS);
  }, [location.search]);

  // Capture state machine.
  //  idle      — no capture requested OR finished and acknowledged
  //  running   — sweep loop in progress
  //  complete  — finished; summary panel + download triggered
  //  cancelled — user clicked Stop
  const [captureStatus, setCaptureStatus] = useState('idle');
  const [captureProgress, setCaptureProgress] = useState({ run: 0, tabIndex: 0, tabId: '', totalTabs: 0, totalRuns: 0 });
  const [captureSummary, setCaptureSummary] = useState(null);
  // Sink status for the server-side ops sink. Lives alongside the
  // capture summary so the UI can show a single combined panel.
  //   idle      — not attempted yet
  //   posting   — POST in flight
  //   ok        — server accepted (snapshot_id returned)
  //   error     — POST failed (manual Retry button surfaces)
  const [sinkStatus, setSinkStatus] = useState('idle');
  const [sinkResult, setSinkResult] = useState(null);  // { snapshot_id, retention_days, expires_at } | { error }
  const captureStartRef = useRef(null);
  // Stash the most recent payload so the Retry button can re-POST
  // without re-running the capture loop.
  const lastPayloadRef = useRef(null);
  // The capture key changes when a fresh ?capture=N URL is loaded;
  // remounting RealEchoScheduler with a new key forces a clean cycler.
  const [captureKey, setCaptureKey] = useState(0);

  // Kick off capture whenever a positive capture param is present and
  // we're not already running. Resetting back to ?capture=0 (or removing
  // the param) returns the page to read-only mode. Demo mode bypasses
  // capture entirely (no real API to instrument).
  useEffect(() => {
    if (isDemoMode()) return;
    if (requestedCaptureRuns > 0 && captureStatus === 'idle') {
      captureStartRef.current = new Date();
      setCaptureSummary(null);
      setCaptureProgress({ run: 1, tabIndex: 0, tabId: '', totalTabs: 0, totalRuns: requestedCaptureRuns });
      setCaptureStatus('running');
      setCaptureKey((k) => k + 1);
    }
  }, [requestedCaptureRuns, captureStatus]);

  const handleCaptureProgress = (p) => {
    setCaptureProgress(p);
  };

  // Sanitize counters / trace state before serialization. Both already
  // carry zero PHI by construction, but we belt-and-braces filter any
  // future drift here so the download contract is enforceable.
  const sanitizeForExport = (capturedRuns, tabsVisited) => {
    const allowedCounterKeys = ['path', '200', '404', '5xx', '0', 'other', 'total', 'lastStatus', 'lastAt'];
    const safeCounters = (getEndpointCounters() || []).map((c) => {
      const out = {};
      for (const k of allowedCounterKeys) if (c[k] !== undefined) out[k] = c[k];
      return out;
    });
    const traceStatesSafe = {};
    for (const [tab, source] of Object.entries(traceStates || {})) {
      // Only ASCII tab keys + source enums. No PHI fields allowed.
      if (/^[a-z_]+$/.test(tab) && typeof source === 'string' && source.length < 32) {
        traceStatesSafe[tab] = source;
      }
    }
    const startedAt = captureStartRef.current ? captureStartRef.current.toISOString() : null;
    const completedAt = new Date().toISOString();
    const durationMs = captureStartRef.current ? (Date.now() - captureStartRef.current.getTime()) : null;
    return {
      schema: 'echo.scheduler.trace.capture.v1',
      build_hash: BUILD_HASH,
      timestamp: completedAt,
      started_at: startedAt,
      completed_at: completedAt,
      duration_ms: durationMs,
      capture_runs_requested: requestedCaptureRuns,
      capture_runs_completed: capturedRuns,
      capture_max_runs: CAPTURE_MAX_RUNS,
      tab_dwell_ms: CAPTURE_TAB_DWELL_MS,
      tabs_visited: tabsVisited,
      trace_string: trace,
      trace_states: traceStatesSafe,
      endpoint_counters: safeCounters,
    };
  };

  const triggerDownload = (payload) => {
    try {
      const ts = (payload.completed_at || new Date().toISOString()).replace(/[:.]/g, '-');
      const fileName = `scheduler-trace-${ts}.json`;
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = fileName;
      a.rel = 'noopener';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Defer revoke so Safari has time to consume the blob URL.
      setTimeout(() => URL.revokeObjectURL(href), 2000);
      return fileName;
    } catch {
      return null;
    }
  };

  // POST the sanitized payload to the server-side sink. Best-effort —
  // failures don't unwind the local download or summary; we surface a
  // status badge + Retry button instead.
  const postToSink = async (payload) => {
    setSinkStatus('posting');
    setSinkResult(null);
    const res = await submitTraceSnapshot(payload);
    if (res?.ok) {
      setSinkStatus('ok');
      setSinkResult({
        snapshotId: res.data?.snapshot_id,
        retentionDays: res.data?.retention_days,
        expiresAt: res.data?.expires_at,
      });
      // Trigger a refresh of the recent-snapshots list if it's loaded.
      setRecentReloadTick((t) => t + 1);
    } else {
      setSinkStatus('error');
      setSinkResult({
        error: res?.error?.message || 'Failed to reach ops sink',
        status: res?.error?.status ?? 0,
      });
    }
  };

  const retrySinkPost = () => {
    if (!lastPayloadRef.current) return;
    postToSink(lastPayloadRef.current);
  };

  const handleCaptureComplete = ({ runs, tabsVisited }) => {
    const payload = sanitizeForExport(runs, tabsVisited);
    lastPayloadRef.current = payload;
    const fileName = triggerDownload(payload);
    setCaptureSummary({
      runs,
      tabsVisited,
      durationMs: payload.duration_ms,
      fileName: fileName || `scheduler-trace-${payload.completed_at}.json`,
      payload,
    });
    setCaptureStatus('complete');
    setCounters(getEndpointCounters());
    // Fire-and-forget the sink POST. Errors surface in the UI badge.
    postToSink(payload);
  };

  const cancelCapture = () => {
    setCaptureStatus('cancelled');
    // Strip the capture param so the cycler unmounts cleanly.
    const params = new URLSearchParams(location.search);
    params.delete('capture');
    navigate({ pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : '' }, { replace: true });
  };

  const dismissCaptureSummary = () => {
    setCaptureSummary(null);
    setCaptureStatus('idle');
    setSinkStatus('idle');
    setSinkResult(null);
    lastPayloadRef.current = null;
    captureStartRef.current = null;
    const params = new URLSearchParams(location.search);
    params.delete('capture');
    navigate({ pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : '' }, { replace: true });
  };

  // ── Recent snapshots disclosure (collapsed by default, admin-only) ──
  const [recentOpen, setRecentOpen] = useState(false);
  const [recentItems, setRecentItems] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [recentError, setRecentError] = useState(null);
  const [recentRetention, setRecentRetention] = useState(null);
  // Bumped whenever a fresh sink POST succeeds so the list refetches.
  const [recentReloadTick, setRecentReloadTick] = useState(0);

  const loadRecent = async () => {
    setRecentLoading(true);
    setRecentError(null);
    const res = await listTraceSnapshots(20);
    if (res?.ok) {
      setRecentItems(res.data?.items || []);
      setRecentRetention(res.data?.retention_days ?? null);
    } else {
      setRecentItems([]);
      setRecentError(res?.error?.message || 'Failed to load recent snapshots');
    }
    setRecentLoading(false);
  };

  useEffect(() => {
    if (recentOpen) loadRecent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentOpen, recentReloadTick]);

  const togglePersist = () => {
    const next = !persisted;
    setEndpointCountersPersistence(next);
    setPersisted(next);
    refresh();
  };

  const refresh = () => {
    setCounters(getEndpointCounters());
    setNowIso(new Date().toISOString());
  };

  useEffect(() => {
    // Light auto-refresh so the page reflects new traffic without
    // requiring a manual click. Cheap — counters live in memory.
    const t = setInterval(refresh, 2500);
    return () => clearInterval(t);
  }, []);

  const tabRows = (trace || '').split('|')
    .filter(Boolean)
    .map((seg) => {
      const [tab, source] = seg.split(':');
      return { tab, source };
    });

  // Build hash — module-level constant set at build time via Create-React-App env var.
  const buildHash = BUILD_HASH;

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="scheduler-trace-page" data-scheduler-trace={trace}>
      <div className="max-w-5xl mx-auto space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] text-slate-400 uppercase font-semibold tracking-wide">Internal · Read-only</p>
            <h1 className="text-[20px] font-semibold text-slate-900 tracking-tight">Scheduler trace</h1>
            <p className="text-[11px] text-slate-500">Tab source states + endpoint hit counters for the current session. No PHI on this page.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="h-8 text-[11px] gap-1" onClick={refresh} data-testid="trace-refresh-btn">
              <RefreshCw className="h-3 w-3" /> Refresh
            </Button>
            <label className="h-8 text-[11px] inline-flex items-center gap-1.5 px-2 rounded-md border border-slate-200 bg-white text-slate-600 cursor-pointer hover:bg-slate-50"
                   data-testid="trace-persist-toggle"
                   data-persist={persisted ? 'on' : 'off'}>
              <input
                type="checkbox"
                checked={persisted}
                onChange={togglePersist}
                className="h-3 w-3"
                data-testid="trace-persist-checkbox"
              />
              Persist (localStorage)
            </label>
            <Button size="sm" variant="ghost" className="h-8 text-[11px] gap-1 text-slate-500" onClick={() => { resetEndpointCounters(); refresh(); }} data-testid="trace-reset-btn">
              Reset counters
            </Button>
            <Link to="/echo/scheduler/_trace?capture=1" data-testid="trace-capture-link" reloadDocument>
              <Button size="sm" variant="outline" className="h-8 text-[11px] gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50" disabled={captureStatus === 'running'}>
                <Download className="h-3 w-3" /> Capture snapshot
              </Button>
            </Link>
            <Link to="/echo/scheduler" data-testid="trace-back-link">
              <Button size="sm" variant="ghost" className="h-8 text-[11px] gap-1">
                <ArrowLeft className="h-3 w-3" /> Back to scheduler
              </Button>
            </Link>
          </div>
        </div>

        {/* ── Capture banner (running / cancelled) ── */}
        {captureStatus === 'running' && (
          <div
            role="status"
            data-testid="trace-capture-banner"
            data-capture-status="running"
            data-capture-run={captureProgress.run}
            data-capture-total-runs={requestedCaptureRuns}
            data-capture-tab={captureProgress.tabId || ''}
            className="rounded-lg border border-emerald-200 bg-emerald-50/70 px-4 py-3 flex items-center justify-between gap-3"
          >
            <div className="min-w-0">
              <p className="text-[12px] font-semibold text-emerald-900">
                Capturing scheduler snapshot…
              </p>
              <p className="text-[11px] text-emerald-700 mt-0.5" data-testid="trace-capture-progress">
                Run <span data-testid="trace-capture-run">{captureProgress.run}</span> of {requestedCaptureRuns}
                {captureProgress.tabId ? ` · tab: ${captureProgress.tabId}` : ''}
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={cancelCapture}
              className="h-8 text-[11px] gap-1 border-rose-300 text-rose-700 hover:bg-rose-50"
              data-testid="trace-capture-cancel-btn"
            >
              <CircleStop className="h-3 w-3" /> Stop
            </Button>
          </div>
        )}

        {/* ── Capture summary (complete) ── */}
        {captureStatus === 'complete' && captureSummary && (
          <div
            role="status"
            data-testid="trace-capture-summary"
            data-capture-status="complete"
            data-capture-runs-completed={captureSummary.runs}
            className="rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3 flex items-start justify-between gap-3"
          >
            <div className="min-w-0">
              <p className="text-[12px] font-semibold text-emerald-900">Capture complete</p>
              <ul className="text-[11px] text-emerald-800 mt-1 space-y-0.5 tabular-nums">
                <li data-testid="trace-capture-summary-runs">Runs: <code>{captureSummary.runs}</code></li>
                <li data-testid="trace-capture-summary-duration">Duration: <code>{Math.round((captureSummary.durationMs || 0) / 100) / 10}s</code></li>
                <li data-testid="trace-capture-summary-file">File: <code>{captureSummary.fileName}</code></li>
                <li
                  data-testid="trace-sink-status"
                  data-sink-status={sinkStatus}
                  className="inline-flex items-center gap-1 mt-0.5"
                >
                  <Server className="h-3 w-3" />
                  Server sink:&nbsp;
                  {sinkStatus === 'posting' && <span>saving…</span>}
                  {sinkStatus === 'ok' && (
                    <span data-testid="trace-sink-ok">
                      saved · <code data-testid="trace-sink-snapshot-id">{sinkResult?.snapshotId?.slice(0, 8) || '—'}</code>
                      {sinkResult?.retentionDays != null && (
                        <span className="text-emerald-700/70"> · retains {sinkResult.retentionDays}d</span>
                      )}
                    </span>
                  )}
                  {sinkStatus === 'error' && (
                    <span data-testid="trace-sink-error" className="text-rose-700">
                      failed — {sinkResult?.error}
                      <button
                        onClick={retrySinkPost}
                        className="ml-2 underline text-rose-800 hover:text-rose-900"
                        data-testid="trace-sink-retry-btn"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', font: 'inherit' }}
                      >
                        Retry
                      </button>
                    </span>
                  )}
                  {sinkStatus === 'idle' && <span className="text-emerald-700/70">—</span>}
                </li>
              </ul>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={dismissCaptureSummary}
              className="h-8 text-[11px] text-emerald-800 hover:bg-emerald-100"
              data-testid="trace-capture-dismiss-btn"
            >
              Dismiss
            </Button>
          </div>
        )}

        {/* ── Hidden scheduler mount that powers the capture sweep ──
            Sits inside the same SchedulerTraceProvider so every tab's
            useReportSchedulerSource() lands in this page's trace map.
            visibility:hidden + position:absolute keeps it off-screen
            without unmounting (so background fetches still fire). */}
        {captureStatus === 'running' && (
          <div
            aria-hidden="true"
            data-testid="trace-capture-hidden-host"
            style={{
              position: 'absolute',
              left: '-10000px',
              top: 0,
              width: '1280px',
              height: '800px',
              overflow: 'hidden',
              visibility: 'hidden',
              pointerEvents: 'none',
            }}
          >
            <RealEchoScheduler
              key={`capture-${captureKey}`}
              captureMode
              captureRuns={requestedCaptureRuns}
              captureTabDwellMs={CAPTURE_TAB_DWELL_MS}
              onCaptureProgress={handleCaptureProgress}
              onCaptureComplete={handleCaptureComplete}
            />
          </div>
        )}

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-semibold text-slate-800">Tab source rollup</p>
            <Badge className="text-[9px] bg-slate-900 text-white border-0">data-scheduler-trace</Badge>
          </div>
          {tabRows.length === 0 ? (
            <p className="text-[11px] text-slate-400" data-testid="trace-rollup-empty">
              No scheduler tab has reported its source yet. Open <code>/echo/scheduler</code> in another tab,
              then refresh this page — sources register as each tab mounts.
            </p>
          ) : (
            <table className="w-full text-[12px]" data-testid="trace-rollup-table">
              <thead className="text-[10px] text-slate-400 uppercase tracking-wide">
                <tr>
                  <th className="text-left py-1.5 pr-3">Tab</th>
                  <th className="text-left py-1.5 pr-3">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {tabRows.map((r) => (
                  <tr key={r.tab} data-testid={`trace-tab-row-${r.tab}`}>
                    <td className="py-1.5 pr-3 font-medium text-slate-700 capitalize">{r.tab}</td>
                    <td className="py-1.5 pr-3">
                      <span className={`text-[10px] font-medium rounded-md border px-1.5 py-0.5 inline-block ${SOURCE_TINT[r.source] || 'bg-slate-50 text-slate-500 border-slate-200'}`}
                            data-testid={`trace-tab-source-${r.tab}`}>
                        {r.source || 'unknown'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-[11px] font-semibold text-slate-800 mb-2">Endpoint hit counters</p>
          {counters.length === 0 ? (
            <p className="text-[11px] text-slate-400" data-testid="trace-counters-empty">
              No <code>/api/scheduler/*</code> calls in this session yet.
            </p>
          ) : (
            <table className="w-full text-[11px]" data-testid="trace-counters-table">
              <thead className="text-[9px] text-slate-400 uppercase tracking-wide">
                <tr>
                  <th className="text-left py-1 pr-2">Path</th>
                  <th className="text-right py-1 pr-2">200</th>
                  <th className="text-right py-1 pr-2">404</th>
                  <th className="text-right py-1 pr-2">5xx</th>
                  <th className="text-right py-1 pr-2">0 (net)</th>
                  <th className="text-right py-1 pr-2">other</th>
                  <th className="text-right py-1 pr-2">Last</th>
                  <th className="text-left py-1 pr-2">At</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-mono">
                {counters.map((c) => (
                  <tr key={c.path} data-testid={`trace-counter-row-${c.path}`}>
                    <td className="py-1 pr-2 text-slate-700 truncate max-w-[28rem]" title={c.path}>{c.path}</td>
                    <td className="py-1 pr-2 text-right"><StatusCell value={c['200']} label="200" tint="bg-emerald-50 text-emerald-700 border-emerald-200" /></td>
                    <td className="py-1 pr-2 text-right"><StatusCell value={c['404']} label="404" tint="bg-amber-50 text-amber-700 border-amber-200" /></td>
                    <td className="py-1 pr-2 text-right"><StatusCell value={c['5xx']} label="5xx" tint="bg-rose-50 text-rose-700 border-rose-200" /></td>
                    <td className="py-1 pr-2 text-right"><StatusCell value={c['0']}   label="0"   tint="bg-rose-50 text-rose-700 border-rose-200" /></td>
                    <td className="py-1 pr-2 text-right"><StatusCell value={c['other']} label="other" tint="bg-slate-50 text-slate-500 border-slate-200" /></td>
                    <td className="py-1 pr-2 text-right text-slate-600 tabular-nums">{c.lastStatus ?? '—'}</td>
                    <td className="py-1 pr-2 text-slate-400 tabular-nums">{c.lastAt ? c.lastAt.slice(11, 19) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Recent snapshots disclosure (collapsed by default) ── */}
        <div className="rounded-xl border border-slate-200 bg-white" data-testid="trace-recent-snapshots">
          <button
            type="button"
            onClick={() => setRecentOpen((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-slate-50 rounded-xl"
            data-testid="trace-recent-toggle"
            data-recent-open={recentOpen ? 'true' : 'false'}
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <span className="inline-flex items-center gap-2 text-[11px] font-semibold text-slate-800">
              {recentOpen ? <ChevronDown className="h-3 w-3 text-slate-500" /> : <ChevronRight className="h-3 w-3 text-slate-500" />}
              Recent ops snapshots
              {recentRetention != null && (
                <span className="text-[10px] text-slate-400 font-normal">· retains {recentRetention}d</span>
              )}
            </span>
            <span className="text-[10px] text-slate-400">{recentOpen ? 'Click to collapse' : 'Click to expand'}</span>
          </button>
          {recentOpen && (
            <div className="px-4 pb-3 pt-1 border-t border-slate-100">
              {recentLoading && (
                <p className="text-[11px] text-slate-400" data-testid="trace-recent-loading">Loading…</p>
              )}
              {recentError && (
                <p className="text-[11px] text-rose-600" data-testid="trace-recent-error">{recentError}</p>
              )}
              {!recentLoading && !recentError && recentItems.length === 0 && (
                <p className="text-[11px] text-slate-400" data-testid="trace-recent-empty">
                  No snapshots submitted to the ops sink yet.
                </p>
              )}
              {!recentLoading && !recentError && recentItems.length > 0 && (
                <table className="w-full text-[11px] mt-1" data-testid="trace-recent-table">
                  <thead className="text-[9px] text-slate-400 uppercase tracking-wide">
                    <tr>
                      <th className="text-left py-1 pr-2">Submitted</th>
                      <th className="text-left py-1 pr-2">Build</th>
                      <th className="text-right py-1 pr-2">Runs</th>
                      <th className="text-right py-1 pr-2">Duration</th>
                      <th className="text-right py-1 pr-2">Counters</th>
                      <th className="text-left py-1 pr-2">By</th>
                      <th className="text-left py-1 pr-2">Snapshot</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-mono">
                    {recentItems.map((it) => (
                      <tr key={it.snapshot_id} data-testid={`trace-recent-row-${it.snapshot_id}`}>
                        <td className="py-1 pr-2 text-slate-600 tabular-nums">{(it.submitted_at || '').replace('T', ' ').slice(0, 19)}</td>
                        <td className="py-1 pr-2 text-slate-700 truncate max-w-[8rem]" title={it.build_hash}>{it.build_hash || '—'}</td>
                        <td className="py-1 pr-2 text-right text-slate-600 tabular-nums">{it.capture_runs_completed ?? '—'}</td>
                        <td className="py-1 pr-2 text-right text-slate-600 tabular-nums">{it.duration_ms != null ? `${Math.round(it.duration_ms / 100) / 10}s` : '—'}</td>
                        <td className="py-1 pr-2 text-right text-slate-600 tabular-nums">{it.counter_count ?? 0}</td>
                        <td className="py-1 pr-2 text-slate-500 truncate max-w-[10rem]" title={it.submitted_by_email}>{it.submitted_by_email || '—'}</td>
                        <td className="py-1 pr-2 text-slate-700"><code>{(it.snapshot_id || '').slice(0, 8)}</code></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        <div className="text-[10px] text-slate-400 flex items-center justify-between" data-testid="trace-footer">
          <div className="inline-flex items-center gap-1">
            <ShieldCheck className="h-3 w-3 text-emerald-500" /> No PHI on this page. Counters {persisted ? 'persist across reloads (localStorage)' : 'reset on hard refresh'}.
          </div>
          <div className="inline-flex items-center gap-3 tabular-nums">
            <span data-testid="trace-build-hash">build: <code>{buildHash}</code></span>
            <span data-testid="trace-now">loaded: <code>{nowIso}</code></span>
          </div>
        </div>
      </div>
    </div>
  );
};

const SchedulerTraceDebug = () => {
  const { user } = useAuth();
  const caps = capabilitiesFor(user || {});
  const allowed = !!caps.canSchedulerAdmin;

  if (!allowed) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 flex items-center justify-center" data-testid="scheduler-trace-forbidden">
        <div className="max-w-md rounded-xl border border-amber-200 bg-amber-50/60 p-4 flex items-start gap-3">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-[12px] font-semibold text-amber-900">Admin access required</p>
            <p className="text-[10px] text-amber-700 mt-1">
              This trace page is restricted to <code>scheduler:admin</code> roles
              (super_admin / admin). Contact your administrator if you need access.
            </p>
            <Link to="/echo/scheduler" className="text-[10px] text-amber-800 underline mt-2 inline-block" data-testid="trace-back-forbidden">
              ← Back to scheduler
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Mount the same SchedulerTraceProvider here so the trace rollup
  // works even if the user navigates directly to /_trace without
  // visiting /echo/scheduler first. Per-tab reports will only fire
  // from the actual scheduler page; on this page the rollup will be
  // empty until that page is opened in another tab.
  return (
    <SchedulerTraceProvider>
      <SchedulerTraceInner />
    </SchedulerTraceProvider>
  );
};

export default SchedulerTraceDebug;
